import * as React from 'react';

import { IconButton, Tooltip, Typography } from '@mui/material';
import {
  Check,
  Copy,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  Volume2,
  VolumeX,
} from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// Per-assistant-message action bar (AI Elements `<Actions>`-style, in MUI):
// copy, regenerate (last message only), read aloud (browser speechSynthesis),
// thumbs up/down feedback, plus the model name from the message metadata
// streamed by the chat API. Token count and gateway cost travel on that same
// metadata, but they are not shown here — cost accounting lives in the admin
// conversation drawer, and the agent chat only names the model.

export type ChatMessageMetadata = {
  model?: string;
  // Present on the streamed finish chunk. Nothing in the chat panel reads
  // them; cost accounting is the admin conversation drawer.
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  cost?: string;
  // 'length' when the reply hit the output cap and was cut off.
  finishReason?: string;
};

const formatModelCaption = (metadata: ChatMessageMetadata): string | null => {
  if (!metadata.model) {
    return null;
  }
  return metadata.model.split('/').pop() ?? metadata.model;
};

type Props = {
  messageText: string;
  metadata?: ChatMessageMetadata;
  canRegenerate: boolean;
  onRegenerate: () => void;
  onFeedback: (rating: 'up' | 'down') => void;
};

export const ChatMessageActions = ({
  messageText,
  metadata,
  canRegenerate,
  onRegenerate,
  onFeedback,
}: Props) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [copied, setCopied] = React.useState(false);
  const [rating, setRating] = React.useState<'up' | 'down' | null>(null);
  const [isSpeaking, setIsSpeaking] = React.useState(false);

  // Stop read-aloud when the message unmounts (chat switch, regenerate).
  React.useEffect(
    () => () => {
      if (isSpeaking) {
        window.speechSynthesis?.cancel();
      }
    },
    [isSpeaking],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permissions); ignore.
    }
  };

  const handleSpeak = () => {
    const synth = window.speechSynthesis;
    if (!synth) {
      return;
    }
    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(messageText);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    setIsSpeaking(true);
    synth.speak(utterance);
  };

  const handleFeedback = (value: 'up' | 'down') => {
    if (rating != null) {
      return;
    }
    setRating(value);
    onFeedback(value);
  };

  const modelCaption = metadata ? formatModelCaption(metadata) : null;
  const iconColor = colors.grey500;

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        marginTop: 2,
        minHeight: 26,
      }}
    >
      <Tooltip title={copied ? t('Copied') : t('Copy')}>
        <IconButton size="small" onClick={handleCopy}>
          {copied ? (
            <Check size={14} color={colors.green600} />
          ) : (
            <Copy size={14} color={iconColor} />
          )}
        </IconButton>
      </Tooltip>
      {canRegenerate && (
        <Tooltip title={t('Regenerate answer')}>
          <IconButton size="small" onClick={onRegenerate}>
            <RefreshCw size={14} color={iconColor} />
          </IconButton>
        </Tooltip>
      )}
      {typeof window !== 'undefined' && !!window.speechSynthesis && (
        <Tooltip title={isSpeaking ? t('Stop reading') : t('Read aloud')}>
          <IconButton size="small" onClick={handleSpeak}>
            {isSpeaking ? (
              <VolumeX size={14} color={colors.primaryMain} />
            ) : (
              <Volume2 size={14} color={iconColor} />
            )}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={t('Good answer')}>
        <span>
          <IconButton
            size="small"
            onClick={() => handleFeedback('up')}
            disabled={rating === 'down'}
          >
            <ThumbsUp
              size={14}
              color={rating === 'up' ? colors.green600 : iconColor}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('Bad answer')}>
        <span>
          <IconButton
            size="small"
            onClick={() => handleFeedback('down')}
            disabled={rating === 'up'}
          >
            <ThumbsDown
              size={14}
              color={rating === 'down' ? colors.errorText : iconColor}
            />
          </IconButton>
        </span>
      </Tooltip>
      {modelCaption && (
        <Typography
          variant="caption"
          sx={{ color: colors.grey400, ml: 0.5, userSelect: 'none' }}
        >
          {modelCaption}
        </Typography>
      )}
    </div>
  );
};
